import { useQuery } from 'react-query';

import type { QueryFunction } from '@frontend/api';
import { useRequest, queryClient } from '@frontend/api';

export type ExtractedTableJson = {
  id: number;
  partFileId: string;
  filename: string;
  printPageId: number | null;
  spreadsheetTabId: number | null;
  pageNumber: number;
  x1: number | null;
  y1: number | null;
  width: number | null;
  height: number | null;
  startRow: number | null;
  startCol: number | null;
  numRows: number | null;
  numCols: number | null;
  authenticatedUrl: string;
  hasBom: boolean;
  hasMissingQuantity: boolean;
  hasInvalidQuantity: boolean;
  hasInconsistentPartNumberIdentifier: boolean;
  hasInconsistentDescriptionIdentifier: boolean;
  confidence: number | null;
};

type QueryParams = {
  uuids: string[];
};

export const EXTRACTED_TABLES_BASE_QUERY_KEY = ['extractedTables', 'list'];

export const getUseGetExtractedTablesKey = (query?: QueryParams) =>
  EXTRACTED_TABLES_BASE_QUERY_KEY.concat((query?.uuids.sort() ?? []).join(','));

export const useGetExtractedTables: QueryFunction<
  ExtractedTableJson[],
  [],
  ReturnType<typeof getUseGetExtractedTablesKey>,
  QueryParams
> = ({ queryOptions, requestOptions, query } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    queryKey: getUseGetExtractedTablesKey(query),
    queryFn: ({ signal }) => {
      return request<ExtractedTableJson[]>(`/parts/extracted_tables`, {
        query,
        init: {
          signal,
        },
      });
    },
  });
};

export const invalidateExtractedTablesQuery = ({
  partFileUuids,
  partFileUuid,
}: {
  partFileUuids?: string[];
  partFileUuid?: string;
}) => {
  if (partFileUuids) {
    queryClient.invalidateQueries(
      getUseGetExtractedTablesKey({ uuids: partFileUuids }),
    );
  } else if (partFileUuid) {
    // use a predicate to invalidate all queries containing the partFileUuid
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === 'extractedTables' &&
        query.queryKey[1] === 'list' &&
        typeof query.queryKey[2] === 'string' &&
        query.queryKey[2].includes(partFileUuid),
    });
  }
};
